import { IconButton } from "@primer/react";
import { XIcon } from "@primer/octicons-react";
import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";

interface IFullScreenModalProps {
    isOpen: boolean;
    title: string;
    onClose: () => void;
    children: ReactNode;
    /** Nội dung footer cố định đáy modal (vd nút Áp dụng / Xóa lọc). */
    footer?: ReactNode;
}

/**
 * Modal tràn màn hình cho mobile (bộ lọc...). Render qua `createPortal` ra `document.body`
 * — Header dùng `position: sticky` tạo stacking context, modal render trong cây Header
 * sẽ bị table cells đè dù z-index cao (theo ghi chú dự án về Primer Dialog legacy).
 *
 * Nút đóng dùng Primer `IconButton` — không tự vẽ lại.
 */
const FullScreenModal = ({ isOpen, title, onClose, children, footer }: IFullScreenModalProps) => {
    // Khóa cuộn nền khi modal mở.
    useEffect(() => {
        if (!isOpen) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [isOpen]);

    // Đóng bằng phím Esc (bàn phím ngoài / dev).
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div className="m-modal" role="dialog" aria-modal="true" aria-label={title}>
            <div className="m-modal-header">
                <span className="m-modal-title">{title}</span>
                <IconButton
                    icon={XIcon}
                    aria-label="Đóng"
                    variant="invisible"
                    size="large"
                    onClick={onClose}
                    sx={{ width: 44, height: 44 }}
                />
            </div>
            <div className="m-modal-body">{children}</div>
            {footer && <div className="m-modal-footer">{footer}</div>}
        </div>,
        document.body
    );
};

export default FullScreenModal;
